import { useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';

import { nextFailureAnnouncement } from '@/lib/ad-sync-failure';
import type { TaskTrackingSummary } from '@/lib/types';
import { toast } from '@/hooks/useToast';

/**
 * Surface a failed live ad-content sync (PUT /ads -> sync_campaign_ads_to_google).
 *
 * The task is dispatched server-side, so there is no task id to poll; the caller's
 * campaign_ads_ga_sync summary is watched instead and a toast fires when its failure count
 * rises (#6297). The first summary seen for a campaign is only a baseline, so a failure that
 * predates this visit never toasts on open (#6583). Mount it once, in the shared campaign layout.
 */
export function useAdSyncFailureToast(campaignId: number, summary: TaskTrackingSummary | undefined): void {
  const posthog = usePostHog();
  const seenFailedRef = useRef<number | null>(null);
  const baselineCampaignRef = useRef(campaignId);

  useEffect(() => {
    if (baselineCampaignRef.current !== campaignId) {
      // The layout can stay mounted across campaigns; another campaign's count is not comparable.
      baselineCampaignRef.current = campaignId;
      seenFailedRef.current = null;
    }
    if (!summary) return;

    const { seenFailed, message } = nextFailureAnnouncement(seenFailedRef.current, summary);
    seenFailedRef.current = seenFailed;
    if (!message) return;

    toast({ description: message, variant: 'destructive' });
    posthog?.capture('campaign_ads_ga_sync_failed', { campaign_id: campaignId });
  }, [summary, campaignId, posthog]);
}
